import React, { PropsWithChildren, useRef, useMemo } from "react";
import { Divider, Grid } from "semantic-ui-react";
import AppForm from "../../components/form/Form";
import { FULLNAME_REGEXP } from "../../app/helpers/regexp";
import { useAppFormContext } from "../../components/form/useAppFormContext";
import { createDurationOptions } from "./durationOptions";
import { prettifyDateWithoutTime, prettifyDuration } from "../../app/helpers/dateHelpers";
import useIndustries from "../../app/hooks/useIndustries";
import useHardcodedCities from "../../app/hooks/useHardcodedCities";

interface Props {
  isEditMode?: boolean;
}

const durationOptions = createDurationOptions(3 * 60, 8 * 60);

const ShadowingForm: React.FC<PropsWithChildren<Props>> = ({ isEditMode }) => {
  const { watch, getValues } = useAppFormContext();
  const { industryOptions } = useIndustries();
  const { cityOptions } = useHardcodedCities();
  const useHqAddress = watch("useHqAddress");
  const isOnline = watch("online");
  const isCreateMode = !isEditMode;
  const initialMaxCapacity = useRef(Math.max(4, getValues("participantsCapacity") ?? 4));
  const currentDuration = watch("visitDuration") as number | null;
  const effectiveDurationOptions = useMemo(() => {
    if (!currentDuration || durationOptions.some((o) => o.value === currentDuration)) {
      return durationOptions;
    }
    const extra = { key: currentDuration, value: currentDuration, text: prettifyDuration(currentDuration) };
    return [...durationOptions, extra].sort((a, b) => a.value - b.value);
  }, [currentDuration]);
  const positionField = (
    <AppForm.Group name="position" label="shadowing.position">
      <AppForm.Input name="position" rules={{ required: true }} maxLength={100} />
    </AppForm.Group>
  );
  const descriptionField = (
    <AppForm.Group name="description" label="shadowing.description">
      <AppForm.TextArea name="description" rules={{ required: true }} maxLength={1000} />
    </AppForm.Group>
  );
  const industryField = (
    <AppForm.Group name="industryIds" label="shadowing.industry">
      <AppForm.Select
        search
        name="industryIds"
        options={industryOptions}
        rules={{ required: true }}
        multiple
        defaultValue={[]}
      />
    </AppForm.Group>
  );
  const maxParticipantField = (
    <AppForm.Group name="participantsCapacity" label="shadowing.max_participants_capacity">
      <AppForm.Integer
        name="participantsCapacity"
        maxLength={10}
        rules={{
          required: true,
          min: {
            value: 1,
            message: "shadowing.errors.min_participants_capacity",
          },
          max: {
            value: initialMaxCapacity.current,
            message: `Dalyvių skaičius negali būti didesnis nei ${initialMaxCapacity.current}`,
          },
        }}
      />
    </AppForm.Group>
  );
  const dateField = (
    <AppForm.Group name="date" label="shadowing.date">
      <AppForm.Date
        name="date"
        rules={{
          required: true,
          validate: {
            futureDate: (value: string) => (value < new Date().toISOString() ? "errors.futureDate" : true),
            maxDateRange: (value: string) => {
              const date = prettifyDateWithoutTime(value);
              return (date >= "2026-03-01" && date <= "2026-08-30") || (date >= "2026-11-23" && date <= "2026-11-27")
                ? true
                : "shadowing.errors.incorrect_date_range";
            },
          },
        }}
      />
    </AppForm.Group>
  );

  const timeField = (
    <AppForm.Group name="time" label="shadowing.time">
      <AppForm.Time
        name="time"
        rules={{
          required: true,
        }}
      />
    </AppForm.Group>
  );

  const minAgeField = (
    <AppForm.Group name="minAge" label="shadowing.min_age">
      <AppForm.Integer
        name="minAge"
        rules={{
          min: {
            value: 5,
            message: "shadowing.errors.min_age",
          },
          max: {
            value: 25,
            message: "shadowing.errors.max_age",
          },
        }}
      />
    </AppForm.Group>
  );
  const durationField = (
    <AppForm.Group name="visitDuration" label="shadowing.visit_duration">
      <AppForm.Select name="visitDuration" options={effectiveDurationOptions} rules={{ required: true }} />
    </AppForm.Group>
  );
  const onlineField = (
    <AppForm.Group name="online">
      <AppForm.Checkbox name="online" label="shadowing.online" />
    </AppForm.Group>
  );
  const fullNameField = (
    <AppForm.Group name="responsiblePerson.fullName" label="shadowing.responsible_person.fullname">
      <AppForm.Input
        name="responsiblePerson.fullName"
        maxLength={50}
        rules={{
          required: true,
          pattern: {
            value: FULLNAME_REGEXP,
            message: "shadowing.errors.fullname",
          },
        }}
      />
    </AppForm.Group>
  );
  const jobTitleField = (
    <AppForm.Group name="responsiblePerson.jobTitle" label="shadowing.responsible_person.job_title">
      <AppForm.Input name="responsiblePerson.jobTitle" rules={{ required: true }} maxLength={50} />
    </AppForm.Group>
  );
  const responsibleEmailField = (
    <AppForm.Group name="responsiblePerson.email" label="shadowing.responsible_person.email">
      <AppForm.Email name="responsiblePerson.email" rules={{ required: true }} maxLength={50} />
    </AppForm.Group>
  );
  const responsiblePhoneField = (
    <AppForm.Group name="responsiblePerson.phoneNumber" label="shadowing.responsible_person.phone">
      <AppForm.Input name="responsiblePerson.phoneNumber" type="tel" maxLength={16} />
    </AppForm.Group>
  );
  const useHqField = (
    <AppForm.Group name="useHqAddress">
      <AppForm.Checkbox name="useHqAddress" label="shadowing.use_hq_address" />
    </AppForm.Group>
  );
  const cityField = (
    <AppForm.Group name="city" label="shadowing.city">
      <AppForm.Select name="city" options={cityOptions} rules={{ required: true }} disableAutoSelect />
    </AppForm.Group>
  );

  const addressField = (
    <AppForm.Group name="address" label="shadowing.address">
      <AppForm.Input name="address" rules={{ required: true }} maxLength={50} />
    </AppForm.Group>
  );

  return (
    <Grid>
      <Grid.Row>
        <Grid.Column>
          {positionField}
          {descriptionField}
          {industryField}
        </Grid.Column>
      </Grid.Row>

      <Grid.Row>
        <Grid.Column width={8}>{dateField}</Grid.Column>
        <Grid.Column width={8}>{timeField}</Grid.Column>
      </Grid.Row>
      <Grid.Row>
        <Grid.Column width={16}>{durationField}</Grid.Column>
      </Grid.Row>
      <Grid.Row>
        <Grid.Column width={16}>{maxParticipantField}</Grid.Column>
      </Grid.Row>

      <Grid.Row>
        <Grid.Column width={16}>{onlineField}</Grid.Column>
      </Grid.Row>

      {isCreateMode && (
        <>
          <Divider />

          <Grid.Row>
            <Grid.Column width={8}>{fullNameField}</Grid.Column>
            <Grid.Column width={8}>{jobTitleField}</Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={8}>{responsibleEmailField}</Grid.Column>
            <Grid.Column width={8}>{responsiblePhoneField}</Grid.Column>
          </Grid.Row>
        </>
      )}

      {!isOnline && (
        <>
          <Divider />

          <Grid.Row>
            <Grid.Column width={16}>{useHqField}</Grid.Column>
          </Grid.Row>

          {!useHqAddress && (
            <Grid.Row>
              <Grid.Column width={8}>{cityField}</Grid.Column>
              <Grid.Column width={8}>{addressField}</Grid.Column>
            </Grid.Row>
          )}
        </>
      )}
    </Grid>
  );
};

export default ShadowingForm;
